import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, IconButton, TextField, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Chip,
} from '@mui/material';
import {
  X, Building2, UsersRound, Plus, CircleAlert,
  Phone, Mail, Pencil, Trash2,
} from 'lucide-react';
import { useNuevoProveedor, ContactoProveedorForm, ContactoProveedorErrors } from '../hooks/useNuevoProveedor';
import { Departamento, Ciudad, ProveedorDetalle } from '../types';
import { ubicacionService } from '../../clientes/services/ubicacionService';

interface Props {
  open: boolean;
  onClose: () => void;
  onGuardado?: () => void;
  proveedorEditar?: ProveedorDetalle | null;
}

function getIniciales(nombres: string, apellidos: string): string {
  return ((nombres.trim()[0] ?? '') + (apellidos.trim()[0] ?? '')).toUpperCase();
}

function ContactCard({
  contacto, onEditar, onEliminar,
}: {
  contacto: ContactoProveedorForm;
  onEditar: (c: ContactoProveedorForm) => void;
  onEliminar: (id: string) => void;
}) {
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '56px 1fr auto auto', alignItems: 'center',
      gap: '12px', p: '12px 16px', borderRadius: '8px',
      border: '1px solid', borderColor: 'grey.200', backgroundColor: '#fff',
      transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.300', backgroundColor: 'primary.50' },
    }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#963781', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'Poppins, sans-serif' }}>
          {getIniciales(contacto.nombres, contacto.apellidos)}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {contacto.nombres} {contacto.apellidos}
          </Typography>
          {contacto.esPrincipal && (
            <Chip label="Principal" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#963781', color: '#fff', '& .MuiChip-label': { px: 1 } }} />
          )}
        </Box>
        {contacto.cargo && (
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.5 }}>
            {contacto.cargo}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {contacto.telefono && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone size={13} color="#666" />
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{contacto.telefono}</Typography>
            </Box>
          )}
          {contacto.correoElectronico && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Mail size={13} color="#666" />
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{contacto.correoElectronico}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <IconButton size="small" onClick={() => onEditar(contacto)} aria-label={`Editar ${contacto.nombres}`} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
        <Pencil size={16} />
      </IconButton>
      <IconButton size="small" onClick={() => onEliminar(contacto.id)} aria-label={`Eliminar ${contacto.nombres}`} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
        <Trash2 size={16} />
      </IconButton>
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontFamily: 'Poppins, sans-serif' },
  '& .MuiInputLabel-root': { fontFamily: 'Poppins, sans-serif' },
};

export function NuevoProveedorDrawer({ open, onClose, onGuardado, proveedorEditar }: Props) {
  const hook = useNuevoProveedor(open, onClose, () => onGuardado?.(), proveedorEditar);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);
  const razonSocialRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ubicacionService.listarDepartamentos().then(setDepartamentos).catch(() => {});
  }, []);

  useEffect(() => {
    if (hook.empresa.departamentoId) {
      ubicacionService.listarCiudades(hook.empresa.departamentoId).then(setCiudades).catch(() => setCiudades([]));
    } else {
      setCiudades([]);
    }
  }, [hook.empresa.departamentoId]);

  useEffect(() => {
    if (open) setTimeout(() => razonSocialRef.current?.focus(), 50);
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') hook.intentarCerrar();
  }, [hook]);

  if (!open) return null;

  const deptoSeleccionado = departamentos.find((d) => d.id === hook.empresa.departamentoId) ?? null;
  const ciudadSeleccionada = ciudades.find((c) => c.id === hook.empresa.ciudadId) ?? null;
  const esEdicion = !!proveedorEditar;

  return (
    <>
      <Box
        onClick={hook.intentarCerrar}
        sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(18,18,22,0.45)', zIndex: 1300 }}
        aria-hidden="true"
      />

      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed', right: 0, top: 0, height: '100dvh',
          width: 'min(1022px, 100vw)', backgroundColor: '#fff',
          zIndex: 1301, display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 4, py: 3, borderBottom: '1px solid #EDE8EE', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box>
            <Typography id="drawer-title" sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '1.25rem', fontWeight: 700, color: '#2C232D' }}>
              {esEdicion ? 'Editar proveedor' : 'Nuevo proveedor'}
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', color: '#756D77', mt: 0.25 }}>
              {esEdicion ? 'Modifica los datos del proveedor y sus contactos.' : 'Completa la información de la empresa y sus contactos.'}
            </Typography>
          </Box>
          <IconButton onClick={hook.intentarCerrar} aria-label="Cerrar drawer" sx={{ color: '#756D77', '&:hover': { background: '#F5EFF5' } }}>
            <X size={24} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Sección Empresa */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: '#F1E3ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={20} color="#74305F" />
              </Box>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#2C232D' }}>
                Empresa
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '20px 24px' }}>
              <TextField
                label="Razón social"
                required
                inputRef={razonSocialRef}
                value={hook.empresa.razonSocial}
                onChange={(e) => hook.updateEmpresa('razonSocial', e.target.value)}
                inputProps={{ maxLength: 160 }}
                error={!!hook.empresaErrors.razonSocial}
                helperText={hook.empresaErrors.razonSocial && (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircleAlert size={14} />{hook.empresaErrors.razonSocial}
                  </Box>
                )}
                sx={{ ...inputSx, gridColumn: '1 / -1' }}
              />

              <TextField
                label="NIT"
                required
                value={hook.empresa.nit}
                onChange={(e) => hook.updateEmpresa('nit', e.target.value)}
                inputProps={{ maxLength: 15 }}
                placeholder="Ej: 900123456"
                error={!!hook.empresaErrors.nit}
                helperText={hook.empresaErrors.nit && (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircleAlert size={14} />{hook.empresaErrors.nit}
                  </Box>
                )}
                sx={inputSx}
              />

              <TextField
                label="Dirección"
                value={hook.empresa.direccion}
                onChange={(e) => hook.updateEmpresa('direccion', e.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />

              <Autocomplete
                options={departamentos}
                getOptionLabel={(o) => o.nombre}
                value={deptoSeleccionado}
                onChange={(_, val) => hook.updateEmpresa('departamentoId', val?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} label="Departamento" sx={inputSx} />
                )}
                noOptionsText="Sin resultados"
              />

              <Autocomplete
                options={ciudades}
                getOptionLabel={(o) => o.nombre}
                value={ciudadSeleccionada}
                onChange={(_, val) => hook.updateEmpresa('ciudadId', val?.id ?? null)}
                disabled={!hook.empresa.departamentoId}
                renderInput={(params) => (
                  <TextField {...params} label="Ciudad" sx={inputSx} />
                )}
                noOptionsText="Selecciona un departamento primero"
              />
            </Box>
          </Box>

          {/* Sección Contactos */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: '#EDF3F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <UsersRound size={20} color="#345B74" />
              </Box>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#2C232D' }}>
                Contactos
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {hook.contactos.map((c) => (
                <ContactCard key={c.id} contacto={c} onEditar={(contacto) => hook.editarContacto(contacto.id)} onEliminar={hook.eliminarContacto} />
              ))}

              {hook.contactoEditing && (
                <ContactoEditorInlineForm
                  contacto={hook.contactoEditing}
                  errors={hook.contactoErrors}
                  onUpdateField={hook.updateContactoEditing}
                  onGuardar={hook.guardarContacto}
                  onCancelar={hook.cancelarContacto}
                />
              )}

              {!hook.contactoEditing && (
                <Box
                  component="button"
                  onClick={hook.iniciarNuevoContacto}
                  data-testid="agregar-contacto-btn"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5,
                    borderRadius: '8px', border: '1.5px dashed #D9AACA', background: 'transparent',
                    color: '#9E427F', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif', transition: 'background 150ms',
                    '&:hover': { background: '#FDF4FB' },
                  }}
                >
                  <Plus size={18} />
                  Agregar contacto
                </Box>
              )}
            </Box>
          </Box>

          {hook.saveError && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '12px 16px', borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C' }}>
              <CircleAlert size={18} />
              <Typography sx={{ fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>{hook.saveError}</Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 4, py: 2.5, borderTop: '1px solid #EDE8EE', display: 'flex', justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={hook.intentarCerrar}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', borderColor: '#D9AACA', color: '#9E427F', borderRadius: '8px', height: 44 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={hook.guardar}
            disabled={hook.saving}
            startIcon={hook.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            data-testid="guardar-proveedor-btn"
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', backgroundColor: '#963781', '&:hover': { backgroundColor: '#7D2D6C' }, borderRadius: '8px', height: 44, minWidth: 130 }}
          >
            {hook.saving ? 'Guardando...' : (esEdicion ? 'Actualizar' : 'Guardar')}
          </Button>
        </Box>
      </Box>

      {/* Dialog confirmación cierre */}
      <Dialog open={hook.confirmandoCierre} onClose={hook.cancelarCierre} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
          ¿Descartar cambios?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, color: '#4B4552' }}>
            Tienes cambios sin guardar. Si cierras ahora, se perderán.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={hook.cancelarCierre} sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', color: '#6B6072' }}>
            Seguir editando
          </Button>
          <Button onClick={hook.confirmarCierre} variant="contained" color="error" sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', borderRadius: '8px' }}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ContactoEditorInlineForm({
  contacto, errors, onUpdateField, onGuardar, onCancelar,
}: {
  contacto: ContactoProveedorForm;
  errors: ContactoProveedorErrors;
  onUpdateField: (field: keyof ContactoProveedorForm, value: unknown) => void;
  onGuardar: () => boolean;
  onCancelar: () => void;
}) {
  return (
    <Box sx={{ border: '1.5px dashed', borderColor: 'grey.300', borderRadius: '8px', p: '20px', position: 'relative', backgroundColor: '#fafafa' }}>
      <IconButton size="small" onClick={onCancelar} aria-label="Cerrar editor de contacto" sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary' }}>
        <X size={20} />
      </IconButton>
      <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', mb: 2, fontFamily: 'Poppins, sans-serif' }}>
        {contacto.nombres === '' ? 'Nuevo contacto' : 'Editar contacto'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
        <TextField label="Nombres" required size="small" value={contacto.nombres}
          onChange={(e) => onUpdateField('nombres', e.target.value)}
          error={!!errors.nombres} helperText={errors.nombres && <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CircleAlert size={14} />{errors.nombres}</Box>}
          inputProps={{ maxLength: 100 }} sx={inputSx} />
        <TextField label="Apellidos" required size="small" value={contacto.apellidos}
          onChange={(e) => onUpdateField('apellidos', e.target.value)}
          error={!!errors.apellidos} helperText={errors.apellidos && <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CircleAlert size={14} />{errors.apellidos}</Box>}
          inputProps={{ maxLength: 100 }} sx={inputSx} />
        <TextField label="Cargo (opcional)" size="small" value={contacto.cargo}
          onChange={(e) => onUpdateField('cargo', e.target.value)}
          inputProps={{ maxLength: 80 }} sx={{ ...inputSx, gridColumn: '1/-1' }} />
        <TextField label="Teléfono" required size="small" value={contacto.telefono}
          onChange={(e) => onUpdateField('telefono', e.target.value)}
          placeholder="+57 300 000 0000"
          error={!!errors.telefono} helperText={errors.telefono && <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CircleAlert size={14} />{errors.telefono}</Box>}
          inputProps={{ maxLength: 20 }} sx={inputSx} />
        <TextField label="Correo electrónico" required size="small" type="email" value={contacto.correoElectronico}
          onChange={(e) => onUpdateField('correoElectronico', e.target.value)}
          error={!!errors.correoElectronico} helperText={errors.correoElectronico && <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CircleAlert size={14} />{errors.correoElectronico}</Box>}
          inputProps={{ maxLength: 120 }} sx={inputSx} />
        <Box sx={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 1 }}>
          <input type="checkbox" id="es-principal" checked={contacto.esPrincipal}
            onChange={(e) => onUpdateField('esPrincipal', e.target.checked)}
            style={{ accentColor: '#963781', width: 16, height: 16 }} />
          <label htmlFor="es-principal" style={{ fontSize: 14, fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
            Definir como contacto principal
          </label>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button variant="text" onClick={onCancelar} sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', color: 'text.secondary' }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => onGuardar()} sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', backgroundColor: '#963781', '&:hover': { backgroundColor: '#7D2D6C' }, height: 40, borderRadius: '6px' }}>
          {contacto.nombres === '' ? 'Agregar contacto' : 'Guardar cambios'}
        </Button>
      </Box>
    </Box>
  );
}
